import { useEffect, useRef, useState } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { X, Download } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import OnboardingPage from "./pages/OnboardingPage";
import ModulesPage from "./pages/ModulesPage";
import ChatPage from "./pages/ChatPage";
import VotingPage from "./pages/VotingPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AgendaPage from "./pages/AgendaPage";
import AgendaAtividadeDetalhe from "./pages/AgendaAtividadeDetalhe";
import LandingPage from "./pages/LandingPage";
import PlanosPage from "./pages/PlanosPage";
import PlanoViewerPage from "./pages/PlanoViewerPage";

// ── Banner de instalação PWA ──
function InstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Não mostrar se já está instalado ou já dispensou
    const dismissed = localStorage.getItem("lumis_install_dismissed");
    if (dismissed) return;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // iOS Safari — mostrar banner manual
      setShow(true);
    } else {
      // Android/Chrome — aguardar evento beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShow(true);
      };
      window.addEventListener("beforeinstallprompt", handler);
      return () => window.removeEventListener("beforeinstallprompt", handler);
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShow(false);
        setDeferredPrompt(null);
      }
    }
    dismiss();
  };

  const dismiss = () => {
    localStorage.setItem("lumis_install_dismissed", "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-banner"
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pt-0 max-w-md mx-auto"
      style={{ bottom: 0 }}
    >
      <div className="bg-white border border-[#DBEAFE] rounded-2xl px-4 py-3 flex items-center gap-3 shadow-[0_-4px_30px_rgba(30,58,138,0.15)]">
        <div className="w-9 h-9 rounded-xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
          <Download size={18} className="text-[#1E3A8A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-gray-900 text-sm font-manrope font-medium leading-tight">Instalar APP agora</p>
          {isIOS ? (
            <p className="text-gray-500 text-xs font-manrope leading-tight mt-0.5">
              Toque em <span className="text-[#1E3A8A]">Compartilhar</span> → "Adicionar à tela de início"
            </p>
          ) : (
            <p className="text-gray-500 text-xs font-manrope leading-tight mt-0.5">
              Acesse o Lumis direto da tela inicial
            </p>
          )}
        </div>
        {!isIOS && (
          <button
            data-testid="install-btn"
            onClick={handleInstall}
            className="bg-[#1E3A8A] text-white text-xs font-manrope font-medium px-3 py-1.5 rounded-full hover:bg-[#172554] active:scale-95 transition-all shrink-0"
          >
            Instalar
          </button>
        )}
        <button
          data-testid="install-dismiss-btn"
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-900 transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function EasterEggReset() {
  const [clicks, setClicks] = useState(0);
  const timerRef = useRef(null);

  const handleClick = () => {
    const next = clicks + 1;
    if (next >= 5) {
      ["lumis_session_id", "lumis_user_name", "lumis_onboarding_complete"].forEach((k) =>
        localStorage.removeItem(k)
      );
      window.location.replace("/");
      return;
    }
    setClicks(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setClicks(0), 2000);
  };

  return (
    <div
      data-testid="easter-egg-reset"
      onClick={handleClick}
      style={{ position: "fixed", top: 0, left: 0, width: 40, height: 40, zIndex: 9999, opacity: 0, cursor: "default" }}
    />
  );
}

const RootRedirect = () => {
  const navigate = useNavigate();
  const hasToken = !!localStorage.getItem("lumis_user_token");

  useEffect(() => {
    if (hasToken) navigate("/agenda", { replace: true });
  }, [hasToken, navigate]);

  if (!hasToken) return <LandingPage />;

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#EEF2FF]">
      <div className="text-[#1E3A8A] text-3xl font-outfit animate-pulse">✦</div>
    </div>
  );
};

// Sessão de usuário final expirada/inválida (401 numa chamada autenticada com
// lumis_user_token) → limpa o token e manda pro login, em vez de deixar a
// página tentando de novo sem nenhum feedback visível pro usuário.
function useExpiredSessionRedirect() {
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const token = localStorage.getItem("lumis_user_token");
        const authHeader = err.config?.headers?.Authorization;
        if (err.response?.status === 401 && token && authHeader === `Bearer ${token}`) {
          localStorage.removeItem("lumis_user_token");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.eject(id);
  }, []);
}

function App() {
  useExpiredSessionRedirect();
  return (
    <BrowserRouter>
      <Toaster />
      <InstallBanner />
      <EasterEggReset />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/modules" element={<ModulesPage />} />
        <Route path="/chat/:moduleId" element={<ChatPage />} />
        <Route path="/voting" element={<VotingPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/agenda/:id" element={<AgendaAtividadeDetalhe />} />
        <Route path="/planos" element={<PlanosPage />} />
        <Route path="/planos/:id" element={<PlanoViewerPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
